import { observable } from 'mobx';
import { formatPrice } from '@this/src/util/price';
import type { MarginResponseArgs } from '../margin2';
import Margin, { convertMarginResponseToArgs } from '../margin2';
import type MarginType from '../organization/margin_type2';
import type { OrderItemPriceDetailResponseArgs } from '../order_item_price_detail2';
import OrderItemPriceDetail, { convertOrderItemPriceDetailResponseToArgs } from '../order_item_price_detail2';
import type { OrderItemOriginalPriceResponseArgs } from '../order_item_original_price2';
import OrderItemOriginalPrice, {
  convertOrderItemOriginalPriceResponseToArgs
} from '../order_item_original_price2';

export type OrderItemPriceResponseArgs = {
  id: number;
  price?: number;
  margin?: MarginResponseArgs;
  flight_price_change_detail?: string;
  order_item_price_details?: OrderItemPriceDetailResponseArgs[];
  order_item_original_prices?: OrderItemOriginalPriceResponseArgs[];
  tax_type_id?: number;
  bulk_ticket_ids?: string[]; // バックエンドの都合によりstring[]になっている
  bulk_ticket_fragment_ids?: number[];
  refund?: number;
  self_loss?: boolean;
  debt_waiver?: boolean;
  loss_workflow_url?: string;
  shareholder_ticket_ids?: number[];
  budget_frame_amount?: number;
};

export type OrderItemPriceArgs = {
  id?: number;
  price?: number;
  margin?: Margin;
  showFee?: boolean;
  flightPriceChangeDetail?: string;
  priceDetails?: OrderItemPriceDetail[];
  originalPrices?: OrderItemOriginalPrice[];
  peopleNum?: number;
  sectionNum?: number;
  marginType?: MarginType;
  taxTypeId?: number;
  bulkTicketIds?: string[];
  bulkTicketFragmentIds?: number[];
  refund?: number;
  selfLoss?: boolean;
  debtWaiver?: boolean;
  lossWorkflowUrl?: string;
  shareholderTicketIds?: number[];
  budgetFrameAmount?: number;
};

export const convertOrderItemPriceResponseToArgs = (
  response: OrderItemPriceResponseArgs,
  showFee: boolean,
  marginType?: MarginType
) => {
  const args: OrderItemPriceArgs = {
    id: response.id,
    price: response.price,
    margin: response.margin ? new Margin(convertMarginResponseToArgs(response.margin)) : undefined,
    showFee,
    flightPriceChangeDetail: response.flight_price_change_detail,
    priceDetails: response.order_item_price_details?.map(
      detail => new OrderItemPriceDetail(convertOrderItemPriceDetailResponseToArgs(detail))
    ),
    originalPrices: response.order_item_original_prices?.map(
      originalPrice => new OrderItemOriginalPrice(convertOrderItemOriginalPriceResponseToArgs(originalPrice))
    ),
    marginType,
    taxTypeId: response.tax_type_id,
    bulkTicketIds: response.bulk_ticket_ids,
    bulkTicketFragmentIds: response.bulk_ticket_fragment_ids,
    refund: response.refund,
    selfLoss: response.self_loss,
    debtWaiver: response.debt_waiver,
    lossWorkflowUrl: response.loss_workflow_url,
    shareholderTicketIds: response.shareholder_ticket_ids,
    budgetFrameAmount: response.budget_frame_amount
  };
  return args;
};

export default class OrderItemPrice {
  @observable id?: number;
  @observable price: number;
  @observable margin?: Margin;
  @observable showFee: boolean;
  @observable flightPriceChangeDetail?: string;
  @observable priceDetails: OrderItemPriceDetail[];
  @observable originalPrices: OrderItemOriginalPrice[];
  @observable peopleNum?: number;
  @observable sectionNum?: number;
  @observable marginType?: MarginType;
  @observable taxTypeId?: number;
  @observable bulkTicketIds: string[];
  @observable bulkTicketFragmentIds: number[];
  @observable refund: number;
  @observable selfLoss?: boolean;
  @observable debtWaiver?: boolean;
  @observable lossWorkflowUrl?: string;
  @observable shareholderTicketIds: number[];
  @observable budgetFrameAmount: number;

  constructor(args: OrderItemPriceArgs) {
    this.id = args.id;
    this.price = args.price ?? 0;
    this.margin = args.margin || new Margin({ amount: 0 });
    this.showFee = args.showFee || false;
    this.flightPriceChangeDetail = args.flightPriceChangeDetail;
    this.priceDetails = args.priceDetails || [];
    this.originalPrices = args.originalPrices || [new OrderItemOriginalPrice({})];
    this.peopleNum = args.peopleNum;
    this.sectionNum = args.sectionNum;
    this.marginType = args.marginType;
    this.taxTypeId = args.taxTypeId;
    this.bulkTicketIds = args.bulkTicketIds || [];
    this.bulkTicketFragmentIds = args.bulkTicketFragmentIds || [];
    this.refund = args.refund || 0;
    this.selfLoss = args.selfLoss;
    this.debtWaiver = args.debtWaiver;
    this.lossWorkflowUrl = args.lossWorkflowUrl;
    this.shareholderTicketIds = args.shareholderTicketIds || [];
    // 暫定処置：海外航空券の時だけbudgetFrameAmountがレスポンスされないため、フロントでフォールバック
    // 恒久的にはサーバー側でbudgetFrameAmountを返す
    this.budgetFrameAmount = args.budgetFrameAmount || this.calcPriceFromDetails || this.price;
  }

  get totalPrice() {
    let p = this.price;
    if (this.showFee && this.margin) {
      p += this.margin.amount;
    }
    return p;
  }

  get description() {
    let d = `\
<料金>
${formatPrice(this.totalPrice)}\
`;
    if (this.priceDetails.length > 1) {
      d += this.priceDetails.map(detail => `\n└ ${detail.name}：${formatPrice(detail.price)}`).join('');
    } else {
      d += `\n└ 商品代金：${formatPrice(this.price)}`;
    }
    if (this.showFee && this.margin && this.margin.amount > 0) {
      d += `\n└ 手数料：${formatPrice(this.margin.amount)}`;
    }
    return d;
  }

  computeMarginAmount(): number {
    if (this.marginType) {
      return this.marginType.calcMarginAmount(this.price, this.peopleNum || 1, this.sectionNum || 1);
    }
    // Default margin calculation (200円 * peopleNum * sectionNum)
    return 200 * (this.peopleNum || 1) * (this.sectionNum || 1);
  }

  calcMarginAmount() {
    const marginAmount = this.computeMarginAmount();
    if (this.margin) {
      this.margin.amount = marginAmount;
    }
  }

  isNeedReCalcMarginAmount(): boolean {
    if (!this.margin) {
      return false;
    }
    const currentMarginAmount = this.computeMarginAmount();
    return this.margin.amount !== currentMarginAmount;
  }

  get validationErrors() {
    return this.originalPrices.flatMap(originalPrice => originalPrice.validationErrors) ?? [];
  }

  get updateParams() {
    // 暫定処置：商品追加時に国内航空券を選択すると必ずエラーになる
    // 原因：GET /arrangement/order_item_categories でshareholder_ticket_idsが空文字になり、
    //      サーバーサイドが必須前提の処理のためエラーになる
    // 恒久対応はサーバーで行い、フロントは戻す想定
    const shareholderTicketIds = this.shareholderTicketIds.filter(id => Number.isInteger(id) && id > 0);
    return {
      price: this.price,
      original_prices: this.originalPrices.map(originalPrice => originalPrice.updateParams),
      price_details: this.priceDetails.map(detail => detail.updateParams),
      refund: this.refund,
      margin: this.margin?.updateParams,
      shareholder_ticket_ids: shareholderTicketIds,
      bulk_ticket_ids: this.bulkTicketIds,
      bulk_ticket_fragment_ids: this.bulkTicketFragmentIds,
      prev_price_id: this.id,
      tax_type_id: this.taxTypeId,
      self_loss: this.selfLoss,
      debt_waiver: this.debtWaiver,
      loss_workflow_url: this.lossWorkflowUrl
    };
  }

  get calcPriceFromDetails() {
    return this.priceDetails?.reduce((acc, e) => acc + e.price, 0) ?? 0;
  }

  get originalTotalPrice() {
    return this.originalPrices?.reduce((acc, e) => acc + e.price, 0) ?? 0;
  }

  get totalPriceWithMargin() {
    let p = this.price;
    if (this.margin) {
      p += this.margin.amount;
    }
    return p;
  }
}
